import { fetchDataWithFetch } from '../../../shared/api/fetchBase';
import type { AccentType } from '../../../shared/types/AccentType';
import type { GalleryThemeMode } from '../../../shared/types/ThemeType';

export type GalleryLayout =
  | 'grid'
  | 'masonry'
  | 'large'
  | 'caption'
  | 'product'
  | 'table';
export type GallerySourceType = 'folder' | 'tags';

export type GalleryTheme = {
  mode: GalleryThemeMode;
  accent: AccentType;
};

export type GalleryAuthorDisplayAs = 'person' | 'company';

export type GalleryAuthor = {
  displayAs?: GalleryAuthorDisplayAs;
  fullName?: string;
  companyName?: string;
  logoFileId?: string;
  /** Read-only: resolved on public gallery API */
  logoUrl?: string;
  logoOriginalUrl?: string;
  occupation?: string;
  email?: string;
  phone?: string;
  description?: string;
};

type CreateGalleryBase = {
  name: string;
  layout: GalleryLayout;
  theme: GalleryTheme;
  author?: GalleryAuthor;
  linkedDatasetId?: string;
  displayColumnIds?: string[];
};

export type CreateFolderGalleryPayload = CreateGalleryBase & {
  sourceType?: 'folder';
  folderId: string;
};

export type CreateTagsGalleryPayload = CreateGalleryBase & {
  sourceType: 'tags';
  rootFolderId: string;
  filterTags: string[];
};

export type CreateGalleryPayload = CreateFolderGalleryPayload | CreateTagsGalleryPayload;

export type CreateGalleryResponse = {
  id: string;
  name: string;
  token: string;
  sourceType: GallerySourceType;
  filterTags?: string[];
  publicUrl: string;
  filesCount: number;
};

export type GalleryListItem = {
  id: string;
  name: string;
  token: string;
  layout: GalleryLayout;
  theme: GalleryTheme;
  publicUrl: string;
  createdAt: string;
  sourceType?: GallerySourceType;
  folderId?: string;
  folderName?: string;
  filterTags?: string[];
  sourceLabel?: string;
  rootFolderId?: string;
  author?: GalleryAuthor;
  linkedDatasetId?: string;
  displayColumnIds?: string[];
};

export type ProfileGalleryWorkspaceSection = {
  rootFolderId: string;
  workspaceName: string;
  workspaceType: 'own' | 'shared';
  accessRole: 'owner' | 'admin';
  canEdit: boolean;
  canDelete: boolean;
  galleries: GalleryListItem[];
};

export type ProfileGalleriesResponse = {
  own: ProfileGalleryWorkspaceSection[];
  shared: ProfileGalleryWorkspaceSection[];
};

export type UpdateGalleryPayload = {
  name: string;
  layout: GalleryLayout;
  theme: GalleryTheme;
  author?: GalleryAuthor;
  linkedDatasetId?: string;
  displayColumnIds?: string[];
};

export async function apiCreateGallery(payload: CreateGalleryPayload): Promise<CreateGalleryResponse> {
  return fetchDataWithFetch<CreateGalleryResponse>('/galleries', {
    method: 'POST',
    data: payload
  });
}

export async function apiListGalleriesByFolder(folderId: string): Promise<GalleryListItem[]> {
  return fetchDataWithFetch<GalleryListItem[]>(`/galleries?folderId=${folderId}`, {
    method: 'GET'
  });
}

export async function apiListGalleriesByRoot(rootFolderId: string): Promise<GalleryListItem[]> {
  return fetchDataWithFetch<GalleryListItem[]>(`/galleries?rootFolderId=${rootFolderId}`, {
    method: 'GET'
  });
}

export async function apiListGalleriesForProfile(): Promise<ProfileGalleriesResponse> {
  return fetchDataWithFetch<ProfileGalleriesResponse>('/galleries/profile', {
    method: 'GET'
  });
}

export async function apiUpdateGallery(
  galleryId: string,
  payload: UpdateGalleryPayload
): Promise<GalleryListItem> {
  return fetchDataWithFetch<GalleryListItem>(`/galleries/${galleryId}`, {
    method: 'PATCH',
    data: payload
  });
}

export async function apiRevokeGallery(galleryId: string): Promise<{ success: boolean; message: string }> {
  return fetchDataWithFetch(`/galleries/${galleryId}`, {
    method: 'DELETE'
  });
}
